import { useSelector } from "react-redux";

import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import _ from "lodash";

import { distributorsKeyFactory } from "@features/distributors/data/distributorQueries";
import { User } from "@models/User";
import { UserRole } from "@models/UserRole";
import client, { useMutateError } from "@services/api";
import { BodyWithId, buildPaginatedQuery } from "@utils/reactQuery";

export const usersKeyFactory = createQueryKeys("users", {
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () => client.get<User>(`users/${id}`).then((res) => res.data),
  }),
  list: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client.get<User[]>("users", { params }).then((res) => res.data),
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client.get<User[]>("users", {
        params,
      }),
  }),
});

export const useUserQuery = (id: string | null | undefined) =>
  useQuery({
    ...usersKeyFactory.detail(id!),
    enabled: !!id,
  });

export const useUserListQuery = () => {
  return useQuery({
    ...usersKeyFactory.list({ skipPagination: true }),
    staleTime: 60_000,
  });
};

export const usePaginatedUsersQuery = buildPaginatedQuery(
  usersKeyFactory.paginated,
  {
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  }
);

export type UserPayload = {
  canUseStripe: boolean;
  email: string;
  vernonBillingId: string | null;
  name: string;
  receivesThresholdEmails: boolean;
  role: UserRole;
  status: "active" | "inactive";
  groups: BodyWithId[];
  supplierId?: string | null;
  distributorId?: number | string | null;
  territories: BodyWithId[];
  channels: BodyWithId[];
  brands: BodyWithId[];
  accountManagerOrganizationIds: (string | number)[];
  purchasingAgentOrganizationIds: (string | number)[];
  supplierUserOrganizationIds: (string | number)[];
  distributorOrganizationIds: (string | number)[];
  channelUsers?: any;
};

const makeUserPayload = ({
  accountManagerOrganizationIds,
  brands,
  purchasingAgentOrganizationIds,
  supplierUserOrganizationIds,
  distributorOrganizationIds,
  supplierId,
  distributorId,
  channelUsers: _channelUsers,
  ...data
}: Partial<UserPayload>) => ({
  __type: "user",
  ...data,
  ...(data.role === "select-account-manager" &&
    accountManagerOrganizationIds && {
      accountManagerOrganizations: accountManagerOrganizationIds.map((id) => ({
        id,
      })),
    }),
  ...(data.role === "purchasing-agent" &&
    purchasingAgentOrganizationIds && {
      purchasingAgentOrganizations: purchasingAgentOrganizationIds.map(
        (id) => ({
          id,
        })
      ),
    }),
  ...(data.role === "supplier" &&
    supplierUserOrganizationIds && {
      supplierUserOrganizations: supplierUserOrganizationIds.map((id) => ({
        id: id,
      })),
      supplier: { id: supplierId },
    }),
  ...(data.role !== "supplier" && { supplier: null }),

  distributorId: distributorId ? +distributorId : null,
  ...(distributorId && {
    distributorUserOrganizations: distributorOrganizationIds?.map((id) => ({
      id,
    })),
  }),

  limitedBrands:
    data.role === "super"
      ? [] // super users should always see all brands
      : (brands?.map(({ id }) => ({ type: "brand", id })) ?? []),

  relationshipNames: [
    "accountManagerOrganizations",
    "purchasingAgentOrganizations",
    "supplierUserOrganizations",
    "distributorUserOrganizations",
    "channels",
    "groups",
    "territories",
    "supplier",
    "limitedBrands",
  ],
});

type CreateUserPayload = Pick<UserPayload, "name" | "email" | "role"> &
  Partial<UserPayload>;

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();
  const mutationError = useMutateError();

  return useMutation({
    mutationFn: (data: CreateUserPayload) =>
      client.post<User>("users", makeUserPayload(data)).then((res) => res.data),
    onSuccess: (user) => {
      queryClient.invalidateQueries({
        queryKey: usersKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: usersKeyFactory.list._def,
      });
      if (user.distributorId) {
        queryClient.invalidateQueries({
          queryKey: distributorsKeyFactory._def,
        });
      }
      return queryClient.setQueryData(
        usersKeyFactory.detail(user.id).queryKey,
        user
      );
    },
    onError: (error, { distributorId }) => {
      mutationError(error, "User Create");

      // Invalidate distributor detail
      if (distributorId) {
        queryClient.invalidateQueries({
          queryKey: distributorsKeyFactory._def,
        });
      }
    },
  });
};

type UpdateUserPayload = { id: string } & Pick<
  UserPayload,
  "channels" | "territories" | "brands"
> &
  Partial<UserPayload>;

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();
  const mutationError = useMutateError();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateUserPayload) =>
      client
        .update<User>(`users/${id}`, makeUserPayload(data))
        .then((res) => res.data),
    onSuccess: (user) => {
      queryClient.invalidateQueries({
        queryKey: usersKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: usersKeyFactory.list._def,
      });
      return queryClient.setQueryData(
        usersKeyFactory.detail(user.id).queryKey,
        user
      );
    },
    onError: (error) => mutationError(error, "User Update"),
  });
};

export const useRemoveMultiOrgUserFromOrgMutation = () => {
  const queryClient = useQueryClient();
  const mutateError = useMutateError();
  const orgId = useSelector((state: any) =>
    Number(state.currentUser.organization.id)
  );

  return useMutation({
    mutationFn: (user: User) =>
      client
        .update(
          `users/${user.id}`,
          makeUserPayload({
            ...user,
            supplierUserOrganizationIds: _.without(
              user.supplierUserOrganizationIds,
              orgId
            ),
            distributorOrganizationIds: _.without(
              user.distributorOrganizationIds,
              orgId
            ),
            purchasingAgentOrganizationIds: _.without(
              user.purchasingAgentOrganizationIds,
              orgId
            ),
            accountManagerOrganizationIds: _.without(
              user.accountManagerOrganizationIds,
              orgId
            ),
          })
        )
        .then((res) => res.data),
    onSuccess: (user) => {
      queryClient.invalidateQueries({
        queryKey: usersKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: usersKeyFactory.list._def,
      });
      if (user.distributorId) {
        queryClient.invalidateQueries({
          queryKey: distributorsKeyFactory.detail(String(user.distributorId))
            .queryKey,
        });
      }
    },
    onError: (error) => mutateError(error, "Remove User from Org"),
  });
};
